/**
 * Compliance constants — acquirer / card-scheme disclosure values.
 *
 * Read BOTH by the legal documents (`content/legal/**`) and by the pre-payment
 * disclosure on wallet top-up (`components/checkout/pre-payment-disclosures.tsx`),
 * so the site can never drift out of sync with what the acquirer approved.
 *
 * The numbers are the same for every brand built from this template. If one
 * changes (processor swap, new refund window), change it HERE — not in the copy.
 *
 * The operating company (name, registration, address) is NOT here: it comes
 * from `legal()` in `lib/legal.config.ts`, which the factory writes per brand.
 * Contact is the brand's one support mailbox — legal asked for a single address
 * everywhere, so do not reintroduce role-specific mailboxes (billing@, business@)
 * that nothing provisions.
 */

import { branding } from "@/lib/branding.config";
import { legalEntity } from "@/lib/legal.config";

/** Trimmed value, or null when unset/blank — callers render the sentence only when set. */
function optional(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

// Widened to string: the template's placeholder is the literal "" and would
// otherwise type the result as `null`.
const phone: string = legalEntity.phone;

export const compliance = {
  /** Payment processor shown to the cardholder before redirect. */
  processorName: "VelvexPay",

  /**
   * Exact card statement descriptor the cardholder sees on their bank
   * statement. MUST match what the acquirer configured for the MID.
   *
   * Set per brand with NEXT_PUBLIC_STATEMENT_DESCRIPTOR (inlined at build).
   * Null when unset: the top-up disclosure, Terms §11 and Privacy §4 then
   * omit the sentence instead of printing a guess.
   */
  statementDescriptor: optional(process.env.NEXT_PUBLIC_STATEMENT_DESCRIPTOR),

  /** The one public mailbox for support, billing, refunds, disputes and privacy. */
  contactEmail: branding.supportEmail,

  /** Public phone from the legal entity. Null when not given — never printed as a placeholder. */
  contactPhone: optional(phone),

  /** Days after a transaction in which a refund request is accepted. */
  refundWindowDays: 14,

  /** First response to a billing ticket or dispute (hours). */
  supportFirstResponseHours: 24,

  /** Decision on a billing dispute after the evidence arrives (business days). */
  disputeResolutionDays: 5,

  /** Final written response to an escalated dispute (business days). */
  escalationResponseDays: 15,

  /** Submitting an approved refund back to the card (business days). */
  refundProcessingDays: 10,

  /** Report an infrastructure incident within this many days so the logs still exist. */
  incidentReportDays: 7,

  /** Wallet credit appears on the account within this many hours of payment. */
  creditDeliveryHours: 1,

  /** Typical time for a started machine to become ready (minutes). */
  provisioningMinutes: 15,

  /**
   * Delay after which the customer may cancel for a full refund (hours).
   * The Terms promise a notice past `creditDeliveryHours` / `provisioningMinutes`;
   * nothing in the code sends it — it is a manual operator duty (HANDOFF).
   */
  delayNotificationHours: 4,

  /** Account data is deleted within this many days of closure. */
  dataRetentionDays: 30,

  /** Maximum backup retention (days). */
  backupRetentionDays: 30,

  /** Support correspondence retention (years). */
  supportRetentionYears: 2,

  /** Answer to a data-protection request (days). */
  privacyResponseDays: 30,
} as const;

/** `tel:` link target for {@link compliance.contactPhone}, or null when there is no phone. */
export const contactPhoneHref = compliance.contactPhone
  ? `tel:${compliance.contactPhone.replace(/[^\d+]/g, "")}`
  : null;
